import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Search, ChevronUp, ChevronDown, X } from 'lucide-react';
import { searchMessages, type Message } from '../api/conversations';
import { getApiClient } from '../api/factory';
import { useT } from '../i18n';

// ANX-077 — панель поиска по сообщениям внутри диалога. Управляет своим
// запросом/результатами/активным индексом; родитель (ConversationView) только
// скроллит к seq (onNavigate) и подсвечивает активный запрос в ленте
// (onActiveQueryChange). Навигация next/prev циклична.

const DEBOUNCE_MS = 220;

export interface ConversationSearchProps {
  conversationId: string;
  onNavigate: (seq: number) => void;
  onActiveQueryChange: (query: string) => void;
  onClose: () => void;
}

export function ConversationSearch({
  conversationId,
  onNavigate,
  onActiveQueryChange,
  onClose,
}: ConversationSearchProps) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Message[]>([]);
  const [index, setIndex] = useState(0);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search. Гонку отсекаем флагом cancelled на каждый эффект.
  useEffect(() => {
    const q = query.trim();
    if (q === '') {
      setResults([]);
      setSearched(false);
      onActiveQueryChange('');
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void searchMessages(getApiClient(), conversationId, q)
        .then((msgs) => {
          if (cancelled) return;
          setResults(msgs);
          setSearched(true);
          setIndex(0);
          onActiveQueryChange(q);
          if (msgs.length > 0) onNavigate(msgs[0].seq);
        })
        .catch(() => {
          if (!cancelled) {
            setResults([]);
            setSearched(true);
          }
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // onNavigate/onActiveQueryChange стабильны у родителя (useCallback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, conversationId]);

  function go(delta: number) {
    if (results.length === 0) return;
    const next = (index + delta + results.length) % results.length;
    setIndex(next);
    onNavigate(results[next].seq);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    go(1);
  }

  const hasResults = results.length > 0;
  return (
    <form
      onSubmit={onSubmit}
      className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1"
    >
      <Search size={14} className="text-slate-400" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        placeholder={t('conversation.search.placeholder')}
        className="w-44 bg-transparent text-sm focus:outline-none"
      />
      <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-slate-500">
        {hasResults
          ? `${index + 1}/${results.length}`
          : searched
            ? t('conversation.search.noResults')
            : ''}
      </span>
      <button
        type="button"
        disabled={!hasResults}
        onClick={() => go(-1)}
        aria-label={t('conversation.search.prev')}
        className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
      >
        <ChevronUp size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        disabled={!hasResults}
        onClick={() => go(1)}
        aria-label={t('conversation.search.next')}
        className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
      >
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('conversation.search.close')}
        className="rounded p-0.5 text-slate-500 hover:bg-slate-100"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </form>
  );
}
